import React, { createContext, useContext, useState, useEffect } from 'react';
import * as api from '../services/api';

const ImpactContext = createContext();

export const useImpact = () => {
    const context = useContext(ImpactContext);
    if (!context) {
        throw new Error('useImpact must be used within an ImpactProvider');
    }
    return context;
};

export const ImpactProvider = ({ children }) => {
    // Initial State Fallback
    const initialData = {
        sectionTitle: "Ciência & Tecnologia",
        title: "Pesquisa & Inovação",
        description: "Integrada ao ecossistema de Ciência, Tecnologia e Inovação da Universidade de Brasília (UnB), a R5 dispõe de capacidade técnico-científica ampliada, que permite desenvolver projetos com elevado grau de complexidade e customização. Com metodologia própria, fundamentada na abordagem de Design Science e Gestão da Inovação, a R5 desenvolve e implementa soluções tecnológicas e modelos avançados de suporte à decisão, para enfrentar problemas complexos em diferentes contextos organizacionais. Dessa forma, transformamos desafios estratégicos em soluções de alto valor agregado e enorme potencial de retorno.",
        image: "/images/hero.png"
    };

    const [impactData, setImpactData] = useState(initialData);
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load Singleton Content (Now the ONLY source of truth)
            const content = await api.getPageContent('impact');
            if (content) {
                // FORCE OVERRIDE: Use new text requested by user
                content.description = "Integrada ao ecossistema de Ciência, Tecnologia e Inovação da Universidade de Brasília (UnB), a R5 dispõe de capacidade técnico-científica ampliada, que permite desenvolver projetos com elevado grau de complexidade e customização. Com metodologia própria, fundamentada na abordagem de Design Science e Gestão da Inovação, a R5 desenvolve e implementa soluções tecnológicas e modelos avançados de suporte à decisão, para enfrentar problemas complexos em diferentes contextos organizacionais. Dessa forma, transformamos desafios estratégicos em soluções de alto valor agregado e enorme potencial de retorno.";
                setImpactData(content);

                // Load Cases exclusively from JSON
                let embeddedCases = Array.isArray(content.cases) ? content.cases : [];

                // Normalize cases
                const normalizedCases = embeddedCases.map(c => ({
                    ...c,
                    id: c.id || `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    image: c.image && c.image.includes('?') ? c.image : `${c.image}?v=${new Date().getTime()}`
                }));

                setCases(normalizedCases);
            }
        } catch (error) {
            console.error("Error loading Impact data:", error);
        } finally {
            setLoading(false);
        }
    };

    const updateImpactData = async (newData) => {
        try {
            const mergedData = { ...impactData, ...newData };
            await api.savePageContent('impact', mergedData);
            setImpactData(mergedData);
        } catch (error) {
            console.error("Error updating impact content:", error);
            throw error;
        }
    };

    // Cases Actions (PURE JSON IMPLEMENTATION)
    const saveCasesToJson = async (newCases) => {
        try {
            const content = await api.getPageContent('impact') || {};
            await api.savePageContent('impact', { ...content, cases: newCases });
        } catch (error) {
            console.error("Error saving cases to JSON:", error);
            throw error;
        }
    };

    const addCase = async (newCase) => {
        try {
            const caseWithId = {
                ...newCase,
                id: `case_${Date.now()}`,
                createdAt: new Date().toISOString()
            };
            const newCaseList = [caseWithId, ...cases];

            setCases(newCaseList);
            await saveCasesToJson(newCaseList);
            return caseWithId;
        } catch (error) {
            console.error("Error adding case:", error);
            throw error;
        }
    };

    const updateCase = async (id, updatedCase) => {
        try {
            const newCaseList = cases.map(c => c.id === id ? { ...c, ...updatedCase } : c);
            setCases(newCaseList);
            await saveCasesToJson(newCaseList);
            return updatedCase;
        } catch (error) {
            console.error("Error updating case:", error);
            throw error;
        }
    };

    const deleteCase = async (id) => {
        try {
            const newCaseList = cases.filter(c => c.id !== id);
            setCases(newCaseList);
            await saveCasesToJson(newCaseList);
        } catch (error) {
            console.error("Error deleting case:", error);
            throw error;
        }
    };

    return (
        <ImpactContext.Provider value={{
            impactData,
            updateImpactData,
            cases,
            addCase,
            updateCase,
            deleteCase,
            loading
        }}>
            {children}
        </ImpactContext.Provider>
    );
};
